import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { fmtPercent } from "../lib/calculations";
import { Target, TrendingUp, Trophy, AlertTriangle, Activity } from "lucide-react";

import HelpIconButton from "./HelpIconButton";
const RECOMMENDATION_STYLES = {
  aggressive: { cls: "bg-[#FF2400] text-white", icon: AlertTriangle },
  premium: { cls: "bg-[#8B5CF6] text-white", icon: Trophy },
  neutral: { cls: "bg-[#15803D] text-white", icon: Activity },
  "low-margin": { cls: "bg-[#CA8A04] text-white", icon: AlertTriangle },
  "high-margin": { cls: "bg-[#002FA7] text-white", icon: Trophy },
};

export default function BidPriceTab() {
  const { t } = useTranslation();
  const { estimate, bidPrice, totals, combined, formatCurrency, updateBidField } = useEstimate();
  const plan = estimate.bidPlan;
  const recStyle = RECOMMENDATION_STYLES[bidPrice.recommendation] || RECOMMENDATION_STYLES.neutral;
  const RecIcon = recStyle.icon;

  return (
    <section className="space-y-6" data-testid="bid-tab">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("bidprice.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("bidprice.title")}
            <HelpIconButton anchor="ch4" testId="help-icon-bidprice" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            {t("bidprice.description")}
          </p>
        </div>
        <div className="border border-gray-300 px-4 py-3 bg-white min-w-[260px]" data-testid="bid-final-card">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            Recommended Bid Price
          </p>
          <p className="font-mono text-3xl font-black text-[#002FA7] leading-none mt-1" data-testid="bid-final-price">
            {formatCurrency(bidPrice.adjustedPrice)}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">
            margin {bidPrice.adjustedMargin.toFixed(1)}% · EV {formatCurrency(bidPrice.expectedValue)}
          </p>
        </div>
      </div>

      {/* INPUTS */}
      <div className="bg-white border border-gray-300 p-5" data-testid="bid-inputs">
        <div className="flex items-center gap-2 mb-4">
          <Target size={16} className="text-[#002FA7]" />
          <h3 className="font-heading text-lg tracking-tight font-bold text-black">Pricing Inputs</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
              Cost basis
            </label>
            <select
              value={plan.pricingBasis}
              onChange={(e) => updateBidField("pricingBasis", e.target.value)}
              data-testid="bid-basis-select"
              className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm bg-white w-full"
            >
              <option value="combined">Combined (DM + BREX)</option>
              <option value="project">Project only (DM)</option>
            </select>
            <p className="text-[10px] text-gray-500 mt-1 font-mono">
              Cost = {formatCurrency(plan.pricingBasis === "project" ? totals.grandTotal : combined.combinedGrandTotal)}
            </p>
          </div>
          <NumberField
            label="Target margin %"
            testid="bid-margin-input"
            value={plan.targetMarginPercent}
            onChange={(v) => updateBidField("targetMarginPercent", v)}
            suffix="%"
          />
          <NumberField
            label="Win probability %"
            testid="bid-winprob-input"
            value={plan.winProbabilityPercent}
            onChange={(v) => updateBidField("winProbabilityPercent", v)}
            suffix="%"
          />
          <NumberField
            label="Competitor price"
            testid="bid-competitor-input"
            value={plan.competitorPrice}
            onChange={(v) => updateBidField("competitorPrice", v)}
            suffix={estimate.currency?.symbol || "$"}
          />
          <NumberField
            label="Strategic adj %"
            testid="bid-strategic-input"
            value={plan.strategicAdjustmentPercent}
            onChange={(v) => updateBidField("strategicAdjustmentPercent", v)}
            suffix="%"
            allowNegative
          />
        </div>
      </div>

      {/* PRICE BUILD-UP */}
      <div className="bg-white border border-gray-300" data-testid="bid-buildup">
        <div className="border-b border-gray-300 p-4">
          <h3 className="font-heading text-lg tracking-tight font-bold text-black">Price Build-up</h3>
        </div>
        <table className="w-full text-sm">
          <tbody>
            <Row label="Estimated cost" value={formatCurrency(bidPrice.cost)} subtle />
            <Row label={`Target margin (${bidPrice.margin}%)`} value={formatCurrency(bidPrice.marginAmount)} />
            <Row label="Target price (cost ÷ (1 − margin))" value={formatCurrency(bidPrice.targetPrice)} bold />
            <Row
              label={`Strategic adjustment (${bidPrice.strategicAdj >= 0 ? "+" : ""}${bidPrice.strategicAdj}%)`}
              value={formatCurrency(bidPrice.adjustedPrice - bidPrice.targetPrice)}
            />
            <Row label="Final bid price" value={formatCurrency(bidPrice.adjustedPrice)} highlight />
            <Row label="Effective margin" value={`${bidPrice.adjustedMargin.toFixed(1)}%`} />
            <Row label={`Expected value (win @ ${bidPrice.winProb}%)`} value={formatCurrency(bidPrice.expectedValue)} />
            <Row label="Expected margin contribution" value={formatCurrency(bidPrice.expectedMargin)} />
          </tbody>
        </table>
      </div>

      {/* COMPARISON & RECOMMENDATION */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-300 p-5" data-testid="bid-competitor-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} className="text-[#FF6B00]" />
            <h3 className="font-heading text-lg tracking-tight font-bold text-black">vs Competitor</h3>
          </div>
          {bidPrice.competitor > 0 ? (
            <>
              <div className="flex items-baseline gap-4 mb-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Your bid</p>
                  <p className="font-mono text-xl font-bold">{formatCurrency(bidPrice.adjustedPrice)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Competitor</p>
                  <p className="font-mono text-xl font-bold">{formatCurrency(bidPrice.competitor)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Delta</p>
                  <p
                    data-testid="bid-competitor-delta"
                    className={`font-mono text-xl font-bold ${bidPrice.competitorDelta >= 0 ? "text-[#FF2400]" : "text-[#15803D]"}`}
                  >
                    {bidPrice.competitorDelta >= 0 ? "+" : ""}{bidPrice.competitorDelta?.toFixed(1)}%
                  </p>
                </div>
              </div>
              <div className="relative bg-gray-100 h-4">
                {(() => {
                  const max = Math.max(bidPrice.adjustedPrice, bidPrice.competitor);
                  const yours = (bidPrice.adjustedPrice / max) * 100;
                  const comp = (bidPrice.competitor / max) * 100;
                  return (
                    <>
                      <div className="absolute top-0 left-0 h-4 bg-[#002FA7]" style={{ width: `${yours}%` }} />
                      <div className="absolute top-0 left-0 h-4 border-r-2 border-black" style={{ width: `${comp}%` }} />
                    </>
                  );
                })()}
              </div>
              <p className="text-xs text-gray-500 font-mono mt-2">
                Blue bar = your bid · Black line = competitor benchmark
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-400" data-testid="bid-competitor-empty">
              Enter a competitor benchmark price to see comparison.
            </p>
          )}
        </div>

        <div className={`p-5 ${recStyle.cls}`} data-testid="bid-recommendation">
          <div className="flex items-center gap-2 mb-2">
            <RecIcon size={18} />
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] opacity-90">
              Recommendation
            </p>
          </div>
          <h3 className="font-heading text-xl tracking-tight font-bold leading-tight">
            {bidPrice.recommendationLabel}
          </h3>
          <ul className="text-xs mt-4 space-y-1 opacity-90 font-mono">
            <li>· Final bid: {formatCurrency(bidPrice.adjustedPrice)} ({fmtPercent(bidPrice.adjustedMargin)} margin)</li>
            <li>· Win-weighted revenue: {formatCurrency(bidPrice.expectedValue)} @ {bidPrice.winProb}% probability</li>
            {bidPrice.competitor > 0 && (
              <li>
                · {bidPrice.competitorDelta >= 0 ? "Above" : "Below"} competitor by{" "}
                {Math.abs(bidPrice.competitorDelta).toFixed(1)}%
              </li>
            )}
          </ul>
        </div>
      </div>
    </section>
  );
}

function NumberField({ label, value, onChange, suffix, allowNegative, testid }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
        {label}
      </label>
      <div className="relative mt-1">
        <input
          type="number"
          min={allowNegative ? undefined : 0}
          step="0.1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="border border-gray-300 rounded-none px-2 py-1.5 font-mono text-sm w-full text-right pr-8"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-mono">{suffix}</span>
      </div>
    </div>
  );
}

function Row({ label, value, bold, subtle, highlight }) {
  const cls = highlight ? "bg-[#002FA7] text-white font-bold" :
              bold ? "font-bold" :
              subtle ? "text-gray-500" : "";
  return (
    <tr className={`border-b border-gray-200 ${cls}`}>
      <td className="py-2 px-3">{label}</td>
      <td className="py-2 px-3 font-mono text-right">{value}</td>
    </tr>
  );
}
